import React, { Fragment } from 'react';
import PropTypes from 'prop-types';
import { Formik } from 'formik';
import validationSchema from './validation';
import Input from '../../../../components/Input';
import Select from '../../../../components/Select';
import ProductionTypes from '../../../../utils/ProductionTypes';
import SaleRentOptions from '../../../../utils/selectors/SaleRentOptions';
import BudgetOptions from '../../../../utils/selectors/BudgetOptions';
import RecordTypeOptions from '../../../../utils/selectors/RecordTypeOptions';
import SoundTypeOptions from '../../../../utils/selectors/SoundTypeOptions';
import RoyaltyLengthOptions from '../../../../utils/selectors/RoyaltyLengthOptions';
import NumberOfCamerasOptions from '../../../../utils/selectors/NumberOfCamerasOptions';
import Textarea from '../../../../components/Textarea';
import UploadFilesField from '../../../../components/UploadFilesField';
import UploadImageField from '../../../../components/UploadImageField';
import ValidatesFormWith from '../../../../utils/ValidatesFormWith';
import TheaterAutocompleteField from '../../../Others/TheaterAutocompleteField';
import Checkbox from '../../../../components/Checkbox';
import { IsAdmin } from '../../../../utils/Roles';
import IsFreePlan from '../../../../utils/IsFreePlan';

class ProductionForm extends React.Component {
  onPublishClick(evt, props) {
    evt.preventDefault();
    props.setFieldValue('publish', true);
    setTimeout(() => {
      props.submitForm();
    }, 500);
  }

  process = (values) => {
    const production = {
      name: values.name,
      description: values.description,
      theaterId: values.theater.id,
      type: values.type,
      year: values.year === '' ? undefined : values.year,
      rights: values.rights,
      saleOrRent: values.salerent,
      budget: values.budget,
      sets: values.sets,
      costumes: values.costumes,
      recordtype: values.recordtype,
      numberofcameras: values.numberofcameras,
      soundtype: values.soundtype,
      royaltylength: values.royaltylength,
      isDigital: this.props.isDigital,
      orchestration: values.orchestration,
      props: values.props,
      titles: values.titles,
      publish: values.publish,
      images: values.images
        ? values.images.map(image => ({
          url: image.url,
          thumbUrl: image.thumbUrl,
        }))
        : [],
      features: [
        {
          type: 'checkbox',
          key: 'childProductions',
          name: 'Producciones para niños',
          value: values.childProductions,
        },
        {
          type: 'checkbox',
          key: 'adultProductions',
          name: 'Producciones para adultos',
          value: values.adultProductions,
        },
        {
          type: 'checkbox',
          key: 'cast',
          name: 'Incluye elenco',
          value: values.cast,
        },
      ],
    };

    if (values.containers) {
      production.features.push({
        type: 'text',
        name: 'Containers',
        key: 'containers',
        value: values.containers,
      });
    }

    values.files.forEach((file) => {
      let { name } = file;
      if ({}.hasOwnProperty.call(file, 'description') && file.description) {
        name = file.description;
      }
      production.features.push({
        type: 'link',
        name,
        value: file.value,
      });
    });

    if (values.regisseur) {
      production.features.push({
        type: 'user-text',
        name: 'Regisseur',
        value: values.regisseur,
      });
    }

    if (values.dressmaker) {
      production.features.push({
        type: 'user-text',
        name: 'Vestuarista',
        value: values.dressmaker,
      });
    }

    if (values.illuminator) {
      production.features.push({
        type: 'user-text',
        name: 'Iluminador',
        value: values.illuminator,
      });
    }

    if (values.multimedia) {
      production.features.push({
        type: 'user-text',
        name: 'Multimedia',
        value: values.multimedia,
      });
    }

    if (values.scenographer) {
      production.features.push({
        type: 'user-text',
        name: 'Escenógrafo',
        value: values.scenographer,
      });
    }

    if (values.stagecraft) {
      production.features.push({
        type: 'user-text',
        name: 'Coreógrafo',
        value: values.stagecraft,
      });
    }

    if (values.video1) {
      production.features.push({
        type: 'video',
        key: 'video1',
        name: 'Primer video',
        value: values.video1,
      });
    }

    if (values.video2) {
      production.features.push({
        type: 'video',
        key: 'video2',
        name: 'Segundo video',
        value: values.video2,
      });
    }

    this.props.onSubmit(production);
  };

  render() {
    const { production } = this.props;

    const isFreePlan = IsFreePlan();
    const isAdmin = IsAdmin();

    if (!production.features) {
      production.features = [];
    }

    const childProductions = production.features.find(f => f.name === 'Producciones para niños');
    const adultProductions = production.features.find(f => f.name === 'Producciones para adultos');
    const cast = production.features.find(f => f.name === 'Incluye elenco');
    const video1 = production.features.find(f => f.key === 'video1');
    const video2 = production.features.find(f => f.key === 'video2');
    const scenographer = production.features.find(f => f.name === 'Escenógrafo');
    const dressmaker = production.features.find(f => f.name === 'Vestuarista');
    const illuminator = production.features.find(f => f.name === 'Iluminador');
    const multimedia = production.features.find(f => f.name === 'Multimedia');
    const stagecraft = production.features.find(f => f.name === 'Coreógrafo');
    const regisseur = production.features.find(f => f.name === 'Regisseur');
    const containers = production.features.find(f => f.name === 'Containers');
    const files = production.features.filter(f => f.type === 'link');

    return (
      <Formik
        enableReinitialize
        initialValues={{
          name: production.name || '',
          type: production.type,
          description: production.description || '',
          theater: production.owner || { name: '' },
          year: production.year || '',
          rights: production.rights,
          salerent: production.saleOrRent,
          budget: production.budget,
          sets: production.sets,
          costumes: production.costumes,
          titles: production.titles,
          props: production.props,
          orchestration: production.orchestration,
          recordtype: production.recordtype,
          numberofcameras: production.numberofcameras,
          soundtype: production.soundtype,
          royaltylength: production.royaltylength,
          childProductions:
            (childProductions && childProductions.value) || false,
          adultProductions:
            (adultProductions && adultProductions.value) || false,
          cast: (cast && cast.value) || false,
          video1: video1 && video1.value,
          video2: video2 && video2.value,
          scenographer:
            (scenographer && scenographer.user && scenographer.user.name) ||
            (scenographer && scenographer.value) ||
            scenographer,
          dressmaker:
            (dressmaker && dressmaker.user && dressmaker.user.name) ||
            (dressmaker && dressmaker.value) ||
            dressmaker,
          illuminator:
            (illuminator && illuminator.user && illuminator.user.name) ||
            (illuminator && illuminator.value) ||
            illuminator,
          multimedia:
            (multimedia && multimedia.user && multimedia.user.name) ||
            (multimedia && multimedia.value) ||
            multimedia,
          stagecraft:
            (stagecraft && stagecraft.user && stagecraft.user.name) ||
            (stagecraft && stagecraft.value) ||
            stagecraft,
          regisseur:
            (regisseur && regisseur.user && regisseur.user.name) ||
            (regisseur && regisseur.value) ||
            regisseur,
          containers: (containers && containers.value) || '',
          files,
          images: production.images,
          publish: false,
        }}
        onSubmit={this.process}
        validate={ValidatesFormWith(validationSchema, true)}
        validateOnChange={false}
        render={props => (
          <form onSubmit={props.handleSubmit}>
            {this.props.title && <h1>{this.props.title}</h1>}
            <div className="search-contents-sidebar">
              <div className="row">
                <div className="col-md-6">
                  <Input label="Nombre" name="name" />
                </div>
                <div className="col-md-6">
                  <Select
                    label="Genero"
                    name="type"
                    options={ProductionTypes}
                  />
                </div>
              </div>
              <div className="row">
                <div className="col-md-6">
                  <Input label="Año de publicación" name="year" type="number" />
                </div>
                <div className="col-md-6">
                  <Select
                    label="Derechos o Royalties"
                    name="rights"
                    options={[
                      { value: 'yes', label: 'Si' },
                      { value: 'no', label: 'No' },
                    ]}
                  />
                </div>
              </div>
              <div className="row">
                <div className="col-md-6">
                  <Select
                    label="Venta o arriendo"
                    name="salerent"
                    options={SaleRentOptions}
                  />
                </div>
                <div className="col-md-6">
                  <Select
                    label="Presupuesto"
                    name="budget"
                    options={BudgetOptions}
                  />
                </div>
              </div>
              <div className="row">
                <div className="col-md-6">
                  <Input label="Primer video / link" name="video1" />
                </div>
                <div className="col-md-6">
                  <Input label="Segundo video / link" name="video2" />
                </div>
              </div>
              <div className="row">
                {isAdmin && (
                  <div className="col-md-6">
                    <TheaterAutocompleteField
                      label="Teatro asociado"
                      name="theater"
                    />
                  </div>
                )}
                {!this.props.isDigital && (
                  <div className="col-md-6">
                    <Input label="Containers" name="containers" type="number" />
                  </div>
                )}
              </div>
              <div className="row">
                <div className="col-md-12">
                  <h2 className="m-b-sm">Equipo técnico</h2>
                </div>
                <div className="col-md-4">
                  <Input label="Regisseur" name="regisseur" />
                </div>
                <div className="col-md-4">
                  <Input label="Escenógrafo" name="scenographer" />
                </div>
                <div className="col-md-4">
                  <Input label="Vestuarista" name="dressmaker" />
                </div>
                <div className="col-md-4">
                  <Input label="Iluminador" name="illuminator" />
                </div>
                <div className="col-md-4">
                  <Input label="Multimedia" name="multimedia" />
                </div>
                <div className="col-md-4">
                  <Input label="Coreófrafo" name="stagecraft" />
                </div>
              </div>
              <div className="row">
                <div className="col-md-12">
                  <h2 className="m-b-sm">Detalles</h2>
                  {this.props.isDigital && (
                    <div className="row">
                      <div className="col-lg-4 col-md-4 col-sm-4">
                        <Select
                          label="Tipo de grabación"
                          name="recordtype"
                          options={RecordTypeOptions}
                        />
                      </div>
                      <div className="col-lg-4 col-md-4 col-sm-4">
                        <Select
                          label="Número de cámaras"
                          name="numberofcameras"
                          options={NumberOfCamerasOptions}
                        />
                      </div>
                      <div className="col-lg-4 col-md-4 col-sm-4">
                        <Select
                          label="Grabación de Sonido"
                          name="soundtype"
                          options={SoundTypeOptions}
                        />
                      </div>
                      <div className="col-lg-4 col-md-4 col-sm-4">
                        <Select
                          label="Tiempo de duración del Royalty o Derechos"
                          name="royaltylength"
                          options={RoyaltyLengthOptions}
                        />
                      </div>
                    </div>
                  )}
                  {!this.props.isDigital && (
                    <div className="row">
                      <div className="col-lg-4 col-md-4 col-sm-4">
                        <Select
                          label="Escenografía/Sets"
                          name="sets"
                          options={SaleRentOptions}
                        />
                        <Select
                          label="Utilería/Props"
                          name="props"
                          options={SaleRentOptions}
                        />
                      </div>
                      <div className="col-lg-4 col-md-4 col-sm-4">
                        <Select
                          label="Vestuario/Costumes"
                          name="costumes"
                          options={SaleRentOptions}
                        />
                        <Select
                          label="Orquestación/Orchestration"
                          name="orchestration"
                          options={SaleRentOptions}
                        />
                      </div>
                      <div className="col-lg-4 col-md-4 col-sm-4">
                        <Select
                          label="Sobretítulos/Titles"
                          name="titles"
                          options={SaleRentOptions}
                        />
                      </div>
                    </div>
                  )}
                </div>
              </div>
              <div className="row">
                <div className="col-md-6">
                  <label className="margin-t-10">Características</label>
                  <Checkbox
                    hideLabel
                    id="childProductions"
                    checkLabel="Producciones para niños / children's production"
                    name="childProductions"
                  />

                  <Checkbox
                    hideLabel
                    checkLabel="Producciones para Adultos / adult's production"
                    id="adultProductions"
                    name="adultProductions"
                  />
                  {!this.props.isDigital && (
                    <Checkbox
                      hideLabel
                      checkLabel="Incluye elenco / includes cast"
                      id="cast"
                      name="cast"
                    />
                  )}
                </div>
              </div>
              <div className="row">
                <div className="col-md-12">
                  {isFreePlan && !isAdmin ? (
                    <div>
                      <label>Descripción</label>
                      <div className="upload-disabled">
                        <span>
                          Para escribir una descripción debes actualizar tu plan
                        </span>
                        <a
                          href="/plans"
                          target="_blank"
                          style={{ minWidth: '200px', marginTop: '10px' }}
                          className="book-btn"
                        >
                          <span className="book-btn-l">
                            <i className="fa fa-link" />
                          </span>
                          <span className="book-btn-r">Cambiar mi plan</span>
                          <div className="clear" />
                        </a>
                      </div>
                    </div>
                  ) : (
                    <Textarea label="Descripción" name="description" />
                  )}
                </div>
              </div>
              <div className="row">
                <div className="col-xs-12 col-md-12">
                  {isFreePlan && !isAdmin ? (
                    <div>
                      <label className="current-file__name control-label">
                        Archivos técnicos
                      </label>
                      <p>
                        1. Dossier de la producción o inventario de los
                        artículos / Please attached dossier or details
                        (inventory) of the articles offered.
                      </p>
                      <p>
                        2. Comunicado de prensa and clippings de la producción /
                        Press release and clippings of the production.
                      </p>
                      <div className="upload-disabled">
                        <span>
                          Para subir archivos o imágenes debes actualizar tu
                          plan
                        </span>
                        <a
                          href="/plans"
                          target="_blank"
                          style={{ minWidth: '200px', marginTop: '10px' }}
                          className="book-btn"
                        >
                          <span className="book-btn-l">
                            <i className="fa fa-link" />
                          </span>
                          <span className="book-btn-r">Cambiar mi plan</span>
                          <div className="clear" />
                        </a>
                      </div>
                    </div>
                  ) : (
                    <UploadFilesField
                      disabled={isFreePlan && !isAdmin}
                      pdf
                      doc
                      name="files"
                      label="Archivos técnicos"
                      subtitle={
                        <Fragment>
                          <p>
                            1. Dossier de la producción o inventario de los
                            artículos / Please attached dossier or details
                            (inventory) of the articles offered.
                          </p>
                          <p>
                            2. Comunicado de prensa and clippings de la
                            producción / Press release and clippings of the
                            production.
                          </p>
                        </Fragment>
                      }
                    />
                  )}
                </div>
              </div>
              <div className="row">
                <div className="col-xs-12">
                  <UploadImageField
                    limit={!isFreePlan || isAdmin ? -1 : 1}
                    label="Imágenes"
                    name="images"
                  />
                </div>
              </div>
              <div className="m-b-md m-t-md">
                <button type="submit" className="book-btn gray">
                  <span className="book-btn-l gray">
                    <i className="fa fa-save" />
                  </span>
                  <span className="book-btn-r">Guardar</span>
                  <div className="clear" />
                </button>

                <button
                  type="submit"
                  onClick={e => this.onPublishClick(e, props)}
                  className="book-btn"
                >
                  <span className="book-btn-l">
                    <i className="fa fa-check" />
                  </span>
                  <span className="book-btn-r">Publicar</span>
                  <div className="clear" />
                </button>
              </div>
            </div>
          </form>
        )}
      />
    );
  }
}

ProductionForm.propTypes = {
  production: PropTypes.object,
  onSubmit: PropTypes.func,
  title: PropTypes.string,
  isDigital: PropTypes.bool,
};

ProductionForm.defaultProps = {
  production: {},
  onSubmit: () => {},
  title: undefined,
  isDigital: false,
};

export default ProductionForm;



// WEBPACK FOOTER //
// ./src/containers/Admin/Production/Form/index.js